import { useMemo } from "react";
import type * as GeoJSON from "geojson";

import {
  CompareSide,
  StoryContent,
  StoryMapData,
  StoryScene,
} from "../../types/storyMap";
import { useProjectsByIds } from "../../api/project";
import {
  useAssetsTilesUrlByProject,
  useFireHistoryByProject,
  useFrozenMapData,
} from "../../api/storyMaps";
import {
  boundaryToFeature,
  decorateFireHistory,
  StoryBoundaryInput,
  StoryCanvasLayer,
} from "./storyLayers";
import { referencedProjectIds, sceneLayerEntries, sideLayerEntries } from "./layerEntries";
import {
  EntryLayerResources,
  Fuel3DItem,
  ProjectPageResources,
  useEntryLayerStack,
  WeatherPanel,
  WindOverlay,
} from "./useEntryLayerStack";
import { Bbox } from "./storyWeather";
import { UnitSystem } from "../../utils/units";
import { buildLegendRows, LegendContext, LegendRow } from "./storyLegend";
import { scopeFromContent, StoryScopeInfo } from "./useStoryScope";

/**
 * Story-wide map data sources, resolved once per page: the frozen
 * content.mapData for public readers, the live endpoints for authed
 * preview/print. Shared by the builder, the viewer and the print page so the
 * public-vs-authed source logic lives in exactly one place.
 *
 * The flat fields describe the story's own project (project stories; and
 * stories published before folder stories existed). `pages` holds the same
 * per cited project for folder and organisation-wide stories.
 */
export interface StorySceneSources {
  /** Raw boundary (Feature/geometry/loose API shape) for fitTo props. */
  boundary: StoryBoundaryInput | null;
  /** Boundary normalised to a Feature (layer data + bbox fitting). */
  boundaryFeature: GeoJSON.Feature | null;
  mapData: StoryMapData | null;
  assetsTilesUrl: string | null;
  /** Already decorated with per-year colours (decorateFireHistory). */
  fireHistory: GeoJSON.FeatureCollection | null;
  /** Cadastral property boundaries (frozen for public, project data authed). */
  properties: GeoJSON.FeatureCollection | null;
  /** Page-level data per cited project, keyed by project id. */
  pages: Record<string, ProjectPageResources>;
  /** Project stories: the project entries without a projectId cite. */
  defaultProjectId: number | null;
  /** Project id → name, for legends and bylines. */
  projectNames: Record<string, string>;
  /**
   * What the map frames when a scene has no camera: the project boundary,
   * or — folder and organisation-wide stories — the cited projects' extent.
   */
  extentFeature: GeoJSON.Feature | null;
  /** True once the assets-tiles lookups have settled (always true public). */
  assetsSettled: boolean;
  /** True once the fire-history fetches have settled (always true public). */
  fireHistorySettled: boolean;
  /**
   * Public: true once the frozen mapData bundle has settled (everything the
   * configured layer entries need lives in it). Always true authed.
   */
  frozenSettled: boolean;
}

const NO_IDS: number[] = [];

export function useStorySceneSources({
  isPublic,
  content,
  scope,
}: {
  isPublic: boolean;
  content: StoryContent | null | undefined;
  /** Authed modes: the story's scope. Public readers derive it from the content. */
  scope?: StoryScopeInfo;
}): StorySceneSources {
  // Frozen geometry lives in a public S3 object referenced by the story
  // (older stories carry it inline); the hook resolves whichever is present.
  const { mapData, settled: mapDataSettled } = useFrozenMapData(content);
  const effectiveScope = useMemo(
    () => (isPublic || !scope ? scopeFromContent(content) : scope),
    [isPublic, scope, content]
  );
  const defaultProjectId = isPublic ? null : effectiveScope.defaultProjectId;

  // Authed: every project the story cites (its own, for a project story),
  // only those the scope allows.
  const projectIds = useMemo(() => {
    if (isPublic) return NO_IDS;
    const cited = referencedProjectIds(content, defaultProjectId);
    return effectiveScope.isScoped ? cited.filter((id) => effectiveScope.byId.has(id)) : cited;
  }, [isPublic, content, defaultProjectId, effectiveScope]);
  const projectsQuery = useProjectsByIds(projectIds);
  const assetsQuery = useAssetsTilesUrlByProject(projectIds, !isPublic);
  const fireHistoryQuery = useFireHistoryByProject(projectIds, !isPublic);

  const pages = useMemo<Record<string, ProjectPageResources>>(() => {
    const out: Record<string, ProjectPageResources> = {};
    if (isPublic) {
      Object.entries(mapData?.projects ?? {}).forEach(([pid, data]) => {
        const boundaryFeature = boundaryToFeature(data.boundary);
        out[pid] = {
          boundary: boundaryFeature,
          properties: data.properties ?? null,
          fireHistory: decorateFireHistory(data.fireHistory),
          assetsTilesUrl: data.assetsTilesUrl ?? null,
          bbox: bboxOfBoundary(boundaryFeature),
        };
      });
      return out;
    }
    projectIds.forEach((id) => {
      const project = projectsQuery.byId[String(id)];
      const boundaryFeature = boundaryToFeature(project?.boundary ?? null);
      const properties = project?.properties;
      out[String(id)] = {
        boundary: boundaryFeature,
        properties:
          properties && properties.length > 0
            ? { type: "FeatureCollection", features: properties }
            : null,
        fireHistory: decorateFireHistory(fireHistoryQuery.byProject[String(id)]),
        assetsTilesUrl: assetsQuery.byProject[String(id)] ?? null,
        bbox: bboxOfBoundary(boundaryFeature),
      };
    });
    return out;
  }, [isPublic, mapData?.projects, projectIds, projectsQuery.byId, fireHistoryQuery.byProject, assetsQuery.byProject]);

  // The story's own project's page (project stories), else the frozen flat fields.
  const own = defaultProjectId !== null ? pages[String(defaultProjectId)] : undefined;
  const boundary: StoryBoundaryInput | null = isPublic
    ? mapData?.boundary ?? null
    : own?.boundary ?? null;
  const boundaryFeature = useMemo(() => boundaryToFeature(boundary), [boundary]);
  const assetsTilesUrl = isPublic ? mapData?.assetsTilesUrl ?? null : own?.assetsTilesUrl ?? null;
  const frozenFireHistory = mapData?.fireHistory;
  const fireHistory = useMemo(
    () => (isPublic ? decorateFireHistory(frozenFireHistory) : own?.fireHistory ?? null),
    [isPublic, frozenFireHistory, own?.fireHistory]
  );
  const properties = isPublic ? mapData?.properties ?? null : own?.properties ?? null;

  const projectNames = useMemo(() => {
    const out: Record<string, string> = {};
    effectiveScope.universe.forEach((p) => {
      out[String(p.id)] = p.name;
    });
    Object.entries(mapData?.projects ?? {}).forEach(([pid, data]) => {
      if (data.name) out[pid] = data.name;
    });
    return out;
  }, [effectiveScope.universe, mapData?.projects]);

  const extentFeature = useMemo<GeoJSON.Feature | null>(() => {
    if (!isPublic && effectiveScope.extentFeature) return effectiveScope.extentFeature;
    if (boundaryFeature) return boundaryFeature;
    const boxes = Object.values(pages)
      .map((p) => p.bbox)
      .filter((b): b is Bbox => !!b);
    if (boxes.length === 0) return null;
    const left = Math.min(...boxes.map((b) => b.left));
    const right = Math.max(...boxes.map((b) => b.right));
    const bottom = Math.min(...boxes.map((b) => b.bottom));
    const top = Math.max(...boxes.map((b) => b.top));
    return {
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: [[[left, bottom], [right, bottom], [right, top], [left, top], [left, bottom]]],
      },
    };
  }, [isPublic, effectiveScope.extentFeature, boundaryFeature, pages]);

  return {
    boundary,
    boundaryFeature,
    mapData,
    assetsTilesUrl,
    fireHistory,
    properties,
    pages,
    defaultProjectId,
    projectNames,
    extentFeature,
    assetsSettled: isPublic ? mapDataSettled : !assetsQuery.isPending,
    fireHistorySettled: isPublic ? mapDataSettled : !fireHistoryQuery.isPending,
    frozenSettled: !isPublic || mapDataSettled,
  };
}

export interface SceneLayerStack {
  layers: StoryCanvasLayer[];
  /**
   * True once every data fetch this stack depends on has settled (loaded or
   * failed) — the print capture pipeline waits on this before mounting the
   * map. The live viewer ignores it (layers stream in as data arrives).
   */
  settled: boolean;
  /** Legend rows for the entries this stack renders (hidden ones omitted). */
  legendRows: LegendRow[];
  /** Wind for the particle overlay (a weather entry with wind on), else null. */
  wind: WindOverlay | null;
  /** Readings for the weather side panel, else null. */
  weatherPanel: WeatherPanel | null;
  /** 3D fuel stands for StoryFuel3DLayers (needs the map instance). */
  fuel3d: Fuel3DItem[];
}

/** Bbox of a boundary Feature, for weather label placement / particles. */
export function bboxOfBoundary(feature: GeoJSON.Feature | null): Bbox | null {
  if (!feature) return null;
  let left = Infinity;
  let right = -Infinity;
  let bottom = Infinity;
  let top = -Infinity;
  const walk = (coords: unknown) => {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === "number" && typeof coords[1] === "number") {
      left = Math.min(left, coords[0]);
      right = Math.max(right, coords[0]);
      bottom = Math.min(bottom, coords[1]);
      top = Math.max(top, coords[1]);
      return;
    }
    coords.forEach(walk);
  };
  walk((feature.geometry as { coordinates?: unknown } | null)?.coordinates);
  return Number.isFinite(left) ? { left, right, bottom, top } : null;
}

/** Legend context from a stack's resolved resources. */
function legendContextFrom(
  resources: EntryLayerResources,
  strategyNames?: Record<string, string>
): LegendContext {
  const customLayers: LegendContext["customLayers"] = {};
  Object.entries(resources.customLayersById ?? {}).forEach(([id, layer]) => {
    customLayers[id] = { name: layer.name, color: layer.color, opacity: layer.opacity };
  });
  return {
    unitSystem: resources.unitSystem,
    customLayers,
    fireHistory: resources.fireHistory,
    strategyNames,
    projectNames: resources.projectNames,
  };
}

/** The page-level inputs every stack hands to useEntryLayerStack. */
function pageInputs(sources: StorySceneSources) {
  return {
    mapData: sources.mapData,
    boundary: sources.boundaryFeature,
    properties: sources.properties,
    fireHistory: sources.fireHistory,
    assetsTilesUrl: sources.assetsTilesUrl,
    pages: sources.pages,
    defaultProjectId: sources.defaultProjectId,
    projectNames: sources.projectNames,
    weatherBbox: bboxOfBoundary(sources.boundaryFeature ?? sources.extentFeature),
  };
}

/**
 * A scene's declarative layer stack from its configured layer entries — the
 * viewer's per-scene assembly, extracted so the print page captures exactly
 * what the viewer renders. Public stories read the frozen mapData bundle and
 * per-entry tiles; authed modes fetch live.
 */
export function useSceneLayerStack({
  scene,
  sources,
  isPublic,
  idPrefix = "story",
  strategyNames,
  weatherElapsedSeconds = null,
  unitSystem = "metric",
}: {
  scene: StoryScene | undefined;
  sources: StorySceneSources;
  isPublic: boolean;
  idPrefix?: string;
  /** Strategy id → name, for treatment legend rows (authed modes). */
  strategyNames?: Record<string, string>;
  /** Animation playback position (weather entries follow it); null = fixed. */
  weatherElapsedSeconds?: number | null;
  unitSystem?: UnitSystem;
}): SceneLayerStack {
  const entries = useMemo(() => sceneLayerEntries(scene), [scene]);
  const page = pageInputs(sources);
  const stack = useEntryLayerStack({
    entries,
    isPublic,
    idPrefix,
    ...page,
    pageSourcesSettled: pageSettledFor(entries, sources),
    weatherElapsedSeconds,
    unitSystem,
  });
  const legendRows = useMemo(
    () => buildLegendRows(entries, legendContextFrom(stack.resources, strategyNames)),
    [entries, stack.resources, strategyNames]
  );
  return {
    layers: stack.layers,
    settled: stack.settled,
    legendRows,
    wind: stack.wind,
    weatherPanel: stack.weatherPanel,
    fuel3d: stack.fuel3d,
  };
}

/**
 * One compare side's layer stack — shared by CompareMapView (live split view)
 * and the print capture pipeline. Sides use the same entry model as scenes,
 * so any layer a scene can show, a side can too.
 */
export function useCompareSideLayers(
  side: CompareSide,
  /** Canonical side id ("before"/"after") — legacy entry ids derive from it. */
  sideId: "before" | "after",
  /** Render prefix for mapbox layer ids (canvases must not collide). */
  prefix: string,
  isPublic: boolean,
  sources: StorySceneSources
): SceneLayerStack {
  const entries = useMemo(() => sideLayerEntries(side, sideId), [side, sideId]);
  const page = pageInputs(sources);
  const stack = useEntryLayerStack({
    entries,
    isPublic,
    idPrefix: prefix,
    ...page,
    pageSourcesSettled: pageSettledFor(entries, sources),
  });
  const legendRows = useMemo(
    () => buildLegendRows(entries, legendContextFrom(stack.resources)),
    [entries, stack.resources]
  );
  return {
    layers: stack.layers,
    settled: stack.settled,
    legendRows,
    wind: stack.wind,
    weatherPanel: stack.weatherPanel,
    fuel3d: stack.fuel3d,
  };
}

/** Assets tile URL per cited project, for the urban-fire building overlay. */
export function assetsTilesByProject(sources: StorySceneSources): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  Object.entries(sources.pages).forEach(([pid, page]) => {
    out[pid] = page.assetsTilesUrl;
  });
  return out;
}

/** Page-level-resource settled gate for the keys these entries actually use. */
function pageSettledFor(
  entries: { key: string }[],
  sources: StorySceneSources
): boolean {
  const keys = new Set(entries.map((e) => e.key));
  return (
    (!keys.has("assets") || sources.assetsSettled) &&
    (!keys.has("fire_history") || sources.fireHistorySettled) &&
    sources.frozenSettled
  );
}
